import React, { useState } from 'react';
import { AddButton } from 'ui';
import { MdAdd } from 'react-icons/md';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

import { DragListContainer } from 'app/area/shared/list-components/drag-list-container';
import { PopupAddNew } from 'app/area/shared/list-components/popup-add-new';

import { createDefaultFilter } from '../utils/create-default-filter';
import { DefaultFiltersTranslation, DefaultFiltersEndpoints } from '../constants';
import { FilterTile } from './filter-tile';
import { DefaultFilter, DefaultFilterValidation, DefaultFilterMetadata } from '../types-store';
import { AddFiltersHandler, DefaultFilterConfig, DeleteAllFiltersHandler, DeleteFilterHandler, UpdateFilterHandler } from '../types';

interface DefaultFiltersProps {
  filters: DefaultFilter[];
  filterMetadata: { [domain: number]: DefaultFilterMetadata };

  addFilters: AddFiltersHandler;
  updateFilter: UpdateFilterHandler;
  deleteFilter: DeleteFilterHandler;
  deleteAllFilters: DeleteAllFiltersHandler;

  config: DefaultFilterConfig;

  endpoints: DefaultFiltersEndpoints;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  locale: string;

  filterValidation: { [domainId: number]: DefaultFilterValidation };
  translation: DefaultFiltersTranslation;
}

const domainPickerId = 'default_filters_domain_picker';

const getKey = (id: number, sequence: number) => isDefinedIncludingZero(id)
  ? `id_${id}` : `sequence_${sequence}`;

export const DefaultFilters: React.FC<DefaultFiltersProps> = ({
  filters, filterMetadata,
  addFilters, updateFilter, deleteFilter, deleteAllFilters,
  config, endpoints, options, loadOptions,
  locale, filterValidation, translation
}) => {

  const loadDomainOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(domainPickerId, endpoints.filterFields, loadOptionsParams);
  };

  const [isOpen, setIsOpen] = useState(false);

  const onOpenAddFilters = () => {
    setIsOpen(true);
  };

  const onCloseAddFilters = () => {
    setIsOpen(false);
  };

  const onAddFilters = (values: ComplexValue<number>[]) => {
    const allFilterIds = filters?.map(filter => filter.domain?.value);
    const newFilters = values
      .filter(value => !allFilterIds.includes(value.value))
      .map(createDefaultFilter);

    addFilters(newFilters);
  };

  return <>
    <DragListContainer footer={<AddButton icon={MdAdd} onClick={onOpenAddFilters}>
      {translation.add_filters}
    </AddButton>}>
      {filters?.map(filter => <FilterTile
        key={getKey(filter.domain?.value, filter.sequence)}
        filter={filter}
        metadata={filterMetadata[filter.domain?.value]}
        updateFilter={updateFilter}
        deleteFilter={deleteFilter}
        deleteAllFilters={deleteAllFilters}
        showRequired={config.showRequired}
        endpoints={endpoints}
        options={options}
        loadOptions={loadOptions}
        locale={locale}
        validation={filterValidation}
        translation={translation} />
      )}
    </DragListContainer>
    {isOpen && <PopupAddNew
      title={translation.add_filters}
      addItems={onAddFilters}
      onClose={onCloseAddFilters}
      selectedValues={filters?.map(filter => filter.domain)}
      options={options?.[domainPickerId]?.codes as ComplexValue<number>[]}
      fetchingOptions={options?.[domainPickerId]?.fetching || false}
      loadOptions={loadDomainOptions}
      descriptionAllLevels={true}
      doneLabel={translation.done}
      searchPlaceholder={translation.search}
      locale={locale}
    />}
  </>;
};
